import {useMemo, useRef} from 'react';
import {assert} from 'assert-ts';
import isEqual from 'lodash/isEqual';
import type {CodeListId} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {isLinkedRoleCodeListId} from 'schemaDefinition';
import type {LinkedRoleCodeListId} from 'schemaDefinition/types/linkTypes';
import {useAppSelector} from 'store/hooks';
import {getLinkedRoleCodelist} from 'schemas/links/functions';
import type {CodeListMap} from '../types';
import {selectCodelists} from '../codelistsSlice';
import type {CodelistRestrictor} from '../contexts';

export function useRestrictedCodelists(
  codelistRestrictor: CodelistRestrictor,
  codelistIds: (CodeListId | LinkedRoleCodeListId)[],
): CodeListMap {
  const localization = useLocalization();
  const codelists = useAppSelector(selectCodelists);
  const cached = useRef<CodeListMap>(undefined);

  return useMemo(() => {
    const codelistMap = codelistIds.reduce<CodeListMap>((acc, id) => {
      if (isLinkedRoleCodeListId(id)) {
        const codelist = getLinkedRoleCodelist(id, localization);
        acc[id] = codelist;
        return acc;
      }

      const codelist = assert(
        codelists[id]?.data,
        `useRestrictedCodelists: codelist ${id} not loaded`,
      );
      acc[id] = codelistRestrictor(codelist);
      return acc;
    }, {});

    if (!isEqual(cached.current, codelistMap)) {
      cached.current = codelistMap;
    }

    return assert(cached.current);
  }, [codelistIds, codelistRestrictor, codelists, localization]);
}
